import { Injectable } from "@angular/core";
import { Observable, of, tap } from "rxjs";
import { QPilotAccessToken, QPilotService, QPilotUser } from "./qpilot.service";
import {
  ChargifySubscription,
  ChargifyProduct,
  ChargifyCoupon,
} from "../models/site.interface";

/**
 * Interface for subscription data storage
 */
export interface QPilotSubscriptionData {
  SubscriptionPlan: ChargifyProduct;
  Coupon: ChargifyCoupon | null;
  Subscription: ChargifySubscription;
}

/**
 * Service for managing user session
 */
@Injectable({
  providedIn: "root",
})
export class SessionService {
  public user: QPilotUser | null = null;
  public accessToken: QPilotAccessToken | null = null;
  public pagesAllowed: string[] = [];
  private announcementDismissedFlag: boolean = false;

  constructor(private readonly qpilotService: QPilotService) {
    // Load session from localStorage if available
    this.loadSession();
  }

  /**
   * Loads the session from localStorage
   */
  public loadSession(): void {
    const tokenStr = localStorage.getItem("session.accessToken");
    if (tokenStr) {
      try {
        this.accessToken = JSON.parse(tokenStr) as QPilotAccessToken;
      } catch (e) {
        console.error("Error parsing access token", e);
        this.clearSession();
      }
    }

    if (this.accessToken?.User) {
      try {
        this.user = this.accessToken.User;
      } catch (e) {
        console.error("Error parsing user", e);
        this.clearSession();
      }
    }
  }

  /**
   * Saves the session to localStorage
   */
  private saveSession(): void {
    if (this.accessToken) {
      localStorage.setItem(
        "session.accessToken",
        JSON.stringify(this.accessToken),
      );
    } else {
      localStorage.removeItem("session.accessToken");
    }

    if (this.user) {
      localStorage.setItem("session.user", JSON.stringify(this.user));
    } else {
      localStorage.removeItem("session.user");
    }
  }

  /**
   * Clears the session
   */
  public clearSession(): void {
    this.accessToken = null;
    this.user = null;
    localStorage.removeItem("session.accessToken");
    localStorage.removeItem("session.user");
  }

  /**
   * Sets the access token
   * @param accessToken Access token
   */
  public setAccessToken(accessToken: QPilotAccessToken): void {
    this.accessToken = accessToken;
    this.saveSession();
  }

  /**
   * Sets the user
   * @param user User
   */
  public setUser(user: QPilotUser): void {
    this.user = user;
    this.saveSession();
  }

  /**
   * Checks if the access token is valid
   * @returns True if the access token is valid
   */
  public accessTokenIsValid(): boolean {
    if (!this.accessToken) {
      return false;
    }
    try {
      const expiresAt = new Date(this.accessToken.ExpiresUtc);
      return expiresAt > new Date();
    } catch (e) {
      console.error("Error checking access token validity", e);
      return false;
    }
  }

  /**
   * Gets the user ID
   * @returns User ID or null if not logged in
   */
  public getUserId(): number | null {
    return this.accessToken?.UserId || null;
  }

  /**
   * Fetches the user from the API
   * @returns Observable with the user
   */
  public fetchUser(): Observable<QPilotUser> {
    if (!this.accessToken || !this.accessTokenIsValid()) {
      return of({} as QPilotUser);
    }

    const userId = this.getUserId();
    if (!userId) {
      return of({} as QPilotUser);
    }

    return this.qpilotService.getUser(userId, this.accessToken).pipe(
      tap((user) => {
        this.setUser(user);
      }),
    );
  }

  /**
   * Fetches the pages allowed for the current user
   * @returns Observable with the pages allowed
   */
  public fetchPagesAllowed(): Observable<string[]> {
    if (!this.accessToken || !this.accessTokenIsValid()) {
      return of([]);
    }

    return this.qpilotService.getPagesAllowed(this.accessToken).pipe(
      tap((pages) => {
        this.pagesAllowed = pages;
      }),
    );
  }

  /**
   * Checks if an announcement has been dismissed
   * @returns True if the announcement has been dismissed
   */
  public announcementDismissed(): boolean {
    return this.announcementDismissedFlag;
  }

  /**
   * Sets the announcement dismissed flag
   * @param dismissed True if the announcement has been dismissed
   */
  public setAnnouncementDismissed(dismissed: boolean): void {
    this.announcementDismissedFlag = dismissed;
  }

  /**
   * Stores subscription data in localStorage for the thank-you page
   * @param subscriptionPlan The selected subscription plan
   * @param coupon The applied coupon (if any)
   * @param subscription The created subscription
   */
  public storeSubscriptionData(
    subscriptionPlan: ChargifyProduct,
    coupon: ChargifyCoupon | null,
    subscription: ChargifySubscription,
  ): void {
    const subscriptionData: QPilotSubscriptionData = {
      SubscriptionPlan: subscriptionPlan,
      Coupon: coupon,
      Subscription: subscription,
    };

    localStorage.setItem("subscriptionData", JSON.stringify(subscriptionData));
  }

  /**
   * Gets stored subscription data from localStorage
   * @returns Subscription data or null if not found
   */
  public getSubscriptionData(): QPilotSubscriptionData | null {
    const data = localStorage.getItem("subscriptionData");
    if (data) {
      try {
        return JSON.parse(data) as QPilotSubscriptionData;
      } catch (e) {
        console.error("Error parsing subscription data:", e);
        localStorage.removeItem("subscriptionData");
      }
    }
    return null;
  }
}
